import { defineJkitTours } from './tour.builder';
import { IJkitTourConfig, IJkitTourStep, IJkitTourTexts } from './tour.model';

export const MY_CUSTOMERS_TOUR_ID = 'myCustomers';
export const CUSTOMER_PAGE_TOUR_ID = 'customerPage';
export const MANAGE_CUSTOMER_PROFILE_TOUR_ID = 'manageCustomerProfile';
export const WORKBOOK_TOUR_ID = 'pricingWorkbook';
export const EDITABLE_WORKBOOK_TOUR_ID = 'editableWorkbook';

export interface IPTourCatalogEntry {
	id: string;
	label: string;
}

export const P_TOUR_CATALOG: readonly IPTourCatalogEntry[] = [
	{ id: MY_CUSTOMERS_TOUR_ID, label: 'My Customers' },
	{ id: CUSTOMER_PAGE_TOUR_ID, label: 'Customer Page' },
	{ id: MANAGE_CUSTOMER_PROFILE_TOUR_ID, label: 'Manage Customer Profile' },
	{ id: WORKBOOK_TOUR_ID, label: 'Pricing Workbook' },
	{ id: EDITABLE_WORKBOOK_TOUR_ID, label: 'Editable Pricing Workbook' }
];

export const P_TOURS_STORAGE_KEY = 'pricing.tours.v1';

export const P_JKIT_TOUR_TEXTS: IJkitTourTexts = {
	skipTour: 'Skip Tour',
	previous: 'Previous',
	next: 'Next',
	complete: 'Finish',
	getStarted: 'Next',
	loading: 'Loading...',
	hotkeysHint: 'Use ← previous, → next, Enter to continue.',
	stepBadgeIntro: 'Welcome',
	stepBadgeComplete: "You're all set!",
	stepBadgeTemplate: 'Step {current} / {total}'
};

const myCustomersSteps: readonly IJkitTourStep[] = [
	{
		id: 'my-customers-welcome',
		title: 'Your Customers Page',
		description: 'Manage your customers, create pricing workbooks, and keep track of active pricing workbooks from one place.',
		isWelcome: true,
		welcomeImageSrc: '/img/my-customers-tour-entry.svg',
		welcomeImageAlt: 'My Customers tour'
	},
	{
		id: 'my-customers-add-customer',
		title: 'Create More Customer Profiles',
		description: 'Create additional customers to begin managing their pricing details and workbooks.',
		target: '[data-tour-anchor="add-customer"]',
		placement: 'bottom'
	},
	{
		id: 'my-customers-create-workbook',
		title: 'Create Pricing Workbooks',
		description: 'Each pricing workbook represents a pricing cycle with a customer. Create a workbook to begin a new cycle.',
		target: '[data-tour-anchor="create-workbook"]',
		placement: 'bottom'
	},
	{
		id: 'my-customers-cards',
		title: 'Customer Cards',
		description:
			'Each customer card provides a quick summary of pricing activity, exchange rates, customer pricing, and user access. Select View to open the customer workspace.',
		target: '[data-tour-anchor="firstCustomerCard"]',
		placement: 'right'
	},
	{
		id: 'my-customers-open-workbooks',
		title: 'Open Pricing Workbooks',
		description: 'Track active pricing workbooks across your customers and quickly return to work that is already in progress.',
		target: '[data-tour-anchor="openWorkbooksGrid"]',
		placement: 'auto'
	}
];

const customerPageSteps: readonly IJkitTourStep[] = [
	{
		id: 'customer-page-welcome',
		title: 'Customer Page',
		description: 'Get a complete view of your customer, including key metrics, active pricing workbooks, and completed pricing cycles.',
		isWelcome: true,
		welcomeImageSrc: '/img/customer-profile-tour-entry.svg',
		welcomeImageAlt: 'Customer page tour'
	},
	{
		id: 'customer-page-manage-profile',
		title: "Manage a Customer's Profile",
		description: 'View and update customer specific pricing, exchange rates, margins, proposed standards, and other profile settings.',
		target: '[data-tour-anchor="customer-profile-manage-profile"]',
		placement: 'bottom'
	},
	{
		id: 'customer-page-create-workbook',
		title: 'Create Pricing Workbooks',
		description: 'Each pricing workbook represents a pricing cycle with a customer. Create a workbook to begin a new cycle.',
		target: '[data-tour-anchor="customer-profile-create-workbook"]',
		placement: 'bottom'
	},
	{
		id: 'customer-page-kpis',
		title: 'Customer Details at a Glance',
		description: 'Quickly see how many users have access and whether exchange rates and customer pricing are configured.',
		target: '[data-tour-anchor="customerKpis"]',
		placement: 'bottom'
	},
	{
		id: 'customer-page-open-workbooks',
		title: 'Open Pricing Workbooks',
		description: 'View active pricing workbooks for this customer and quickly return to pricing cycles already in progress.',
		target: '[data-tour-anchor="customerOpenWorkbooks"]',
		placement: 'auto'
	},
	{
		id: 'customer-page-archived-workbooks',
		title: 'Archived Pricing Workbooks',
		description:
			'Archived pricing workbooks for this customer appear here. Review past workbooks whenever you need to reference previous pricing cycles.',
		target: '[data-tour-anchor="customerArchivedWorkbooks"]',
		placement: 'auto'
	}
];

const manageCustomerProfileSteps: readonly IJkitTourStep[] = [
	{
		id: 'manage-profile-welcome',
		title: 'Customer Profile Management',
		description: 'Manage customer specific settings and pricing details that support pricing workbooks throughout each pricing cycle.',
		isWelcome: true,
		welcomeImageSrc: '/img/manage-customer-profile-tour-entry.svg',
		welcomeImageAlt: 'Customer profile management tour'
	},
	{
		id: 'manage-profile-details',
		title: 'Profile Details',
		description: 'Update the customer profile name and manage the material groups linked to this customer.',
		target: '[data-tour-anchor="manage-profile-tab-profile"]',
		placement: 'bottom'
	},
	{
		id: 'manage-profile-users',
		title: 'Manage User Access',
		description: 'Add or remove users to control who can access this customer and their pricing information.',
		target: '[data-tour-anchor="manage-profile-tab-users"]',
		placement: 'bottom'
	},
	{
		id: 'manage-profile-proposed-std',
		title: 'Proposed STD Logic',
		description: "Select the Proposed STD logic that will be applied when pricing a customer's workbooks.",
		target: '[data-tour-anchor="manage-profile-tab-proposed-std"]',
		placement: 'bottom'
	},
	{
		id: 'manage-profile-margins',
		title: 'Customer Margins',
		description: "Configure customer specific margins to support pricing calculations for a customer's workbooks.",
		target: '[data-tour-anchor="manage-profile-tab-margins"]',
		placement: 'bottom'
	},
	{
		id: 'manage-profile-exchange-rates',
		title: 'Customer Exchange Rates',
		description: 'Set exchange rates for this customer to support pricing across different currencies.',
		target: '[data-tour-anchor="manage-profile-tab-exchange-rates"]',
		placement: 'bottom'
	},
	{
		id: 'manage-profile-pricing',
		title: 'Customer Pricing',
		description: "Import customer specific pricing to populate Final PP2 in a customer's pricing workbooks.",
		target: '[data-tour-anchor="manage-profile-tab-pricing"]',
		placement: 'bottom'
	}
];

const workbookSteps: readonly IJkitTourStep[] = [
	{
		id: 'workbook-welcome',
		title: 'Pricing Workbook',
		description: 'Get a complete view of your pricing workbook, including key metrics for the overall workbook and each assembly.',
		isWelcome: true,
		welcomeImageSrc: '/img/workbook-page-tour-entry.svg',
		welcomeImageAlt: 'Pricing workbook tour'
	},
	{
		id: 'workbook-details',
		title: 'Workbook Details',
		description: 'See the customer, workbook name, and current status of the pricing cycle at a glance.',
		target: '[data-tour-anchor="pageHeaderLead"]',
		placement: 'bottom'
	},
	{
		id: 'workbook-options',
		title: 'Workbook Options',
		description: 'Manage workbook assemblies, export a flattened version of the workbook, or delete the workbook.',
		target: '[data-tour-anchor="workbook-options"]',
		placement: 'bottom'
	},
	{
		id: 'workbook-complete',
		title: 'Complete Your Workbook',
		description: 'Complete the workbook when pricing is finalized and you are ready to close the pricing cycle.',
		target: '[data-tour-anchor="workbook-complete"]',
		placement: 'bottom'
	},
	{
		id: 'workbook-summary-tab',
		title: 'Workbook Summary',
		description: 'Get a high level view of your pricing workbook, including key metrics and pricing impacts across the workbook.',
		target: '#p-tabset-tab-summary',
		placement: 'bottom'
	},
	{
		id: 'workbook-part-details-tab',
		title: 'Edit Pricing Details',
		description: 'Move from the workbook summary to detailed pricing where you can review and update your workbook.',
		target: '#p-tabset-tab-part-details',
		placement: 'bottom'
	},
	{
		id: 'workbook-kpis',
		title: 'Workbook at a Glance',
		description:
			'Compare current and proposed pricing, review the overall price change, and see how many assemblies and parts are included.',
		target: '[data-tour-anchor="workbookKpis"]',
		placement: 'bottom'
	},
	{
		id: 'workbook-assemblies',
		title: 'Assembly Level Impacts',
		description: 'See how each assembly contributes to the workbook with pricing, price changes, and progress at the assembly level.',
		target: '[data-tour-anchor="workbookAssemblies"]',
		placement: 'auto'
	}
];

const editableWorkbookSteps: readonly IJkitTourStep[] = [
	{
		id: 'editable-workbook-welcome',
		title: 'Pricing Workbook',
		description: 'Review and update pricing details for the parts included in your workbook as you work through the pricing cycle.',
		isWelcome: true,
		welcomeImageSrc: '/img/workbook-bucket-page-tour-entry.svg',
		welcomeImageAlt: 'Editable pricing workbook tour'
	},
	{
		id: 'editable-workbook-details',
		title: 'Workbook Details',
		description: 'See the customer, workbook name, and parts bucket name at a glance.',
		target: '[data-tour-anchor="pageHeaderLead"]',
		placement: 'bottom'
	},
	{
		id: 'editable-workbook-clear-filters',
		title: 'Clear All Filters',
		description: 'Clear all filters applied to the grid to quickly return to the full workbook view.',
		target: '[data-tour-anchor="allPartsClearFilters"]',
		placement: 'bottom'
	},
	{
		id: 'editable-workbook-saved-views',
		title: 'Save Custom Grid Views',
		description: 'Customize your grid by arranging, resizing, pinning, or hiding columns. Save up to five views for quick access.',
		target: '[data-tour-anchor="allPartsSavedViews"]',
		placement: 'bottom'
	},
	{
		id: 'editable-workbook-import',
		title: 'Import Bulk Updates',
		description: 'Import updates to apply pricing changes across multiple parts in your workbook at once.',
		target: '[data-tour-anchor="allPartsImport"]',
		placement: 'bottom'
	},
	{
		id: 'editable-workbook-export',
		title: 'Export Pricing Workbook',
		description: 'Export your pricing workbook to review or work with pricing details outside of the application.',
		target: '[data-tour-anchor="allPartsExport"]',
		placement: 'bottom'
	},
	{
		id: 'editable-workbook-part-details',
		title: 'Work With Part Details',
		description:
			'Edit values in columns with blue headers. Right click a row to quickly view additional data, or select a part for more details.',
		target: '[data-tour-anchor="allPartsGrid"]',
		placement: 'auto'
	},
	{
		id: 'editable-workbook-review',
		title: 'Review Pricing Details',
		description:
			'View tooltips on calculated columns to understand how values are calculated. Use the checkboxes to flag parts that need further review.',
		target: '[data-tour-anchor="allPartsGrid"]',
		placement: 'auto'
	}
];

export const P_JKIT_TOURS: readonly IJkitTourConfig[] = defineJkitTours(
	{
		id: MY_CUSTOMERS_TOUR_ID,
		entry: '/my-customers',
		priority: 1,
		steps: myCustomersSteps
	},
	{
		id: CUSTOMER_PAGE_TOUR_ID,
		entry: '/my-customers',
		priority: 1,
		steps: customerPageSteps
	},
	{
		id: MANAGE_CUSTOMER_PROFILE_TOUR_ID,
		entry: '/my-customers',
		priority: 2,
		steps: manageCustomerProfileSteps
	},
	{
		id: WORKBOOK_TOUR_ID,
		entry: '/workbook',
		priority: 1,
		steps: workbookSteps
	},
	{
		id: EDITABLE_WORKBOOK_TOUR_ID,
		entry: '/workbook',
		priority: 2,
		steps: editableWorkbookSteps
	}
);
